import { create } from "zustand";
import { persist } from "zustand/middleware";
// VAD artık hook'larda kullanılıyor
import useUIPreferencesStore from "@/store/use-ui-preferences-store";
import axios from "axios";

interface VoiceChatState {
  // UI States
  isVoiceChatActive: boolean;
  isVoiceChatVisible: boolean;
  isVoiceChatMinimized: boolean;
  isListening: boolean;
  isProcessing: boolean;
  isWaitingForResponse: boolean;

  // Audio & Transcript States
  transcript: string;
  error: string;
  audioLevel: number;

  // TTS States
  isSpeaking: boolean;
  isTTSLoading: boolean;

  // Widget & Feature States
  hasActiveWidget: boolean;
  autoMinimizeOnProducts: boolean;
  isMobile: boolean;
}

interface VoiceChatActions {
  // Voice Chat Controls
  startVoiceChat: () => Promise<void>;
  stopVoiceChat: () => void;
  closeVoiceChat: () => void;
  hideVoiceChat: () => void;
  showVoiceChat: () => void;
  minimizeVoiceChat: () => void;
  maximizeVoiceChat: () => void;

  // Transcript Actions
  clearTranscript: () => void;
  resetTranscript: () => void;
  submitTranscript: (text?: string) => void;

  // TTS Actions
  speak: (text: string) => Promise<void>;
  stopSpeaking: () => void;

  // State Setters
  setHasActiveWidget: (value: boolean) => void;
  setAutoMinimizeOnProducts: (value: boolean) => void;
  setIsListening: (value: boolean) => void;
  setIsProcessing: (value: boolean) => void;
  setIsWaitingForResponse: (value: boolean) => void;
  setTranscript: (value: string) => void;
  setError: (value: string) => void;
  setAudioLevel: (value: number) => void;
  setIsSpeaking: (value: boolean) => void;
  setIsTTSLoading: (value: boolean) => void;
  setIsMobile: (value: boolean) => void;

  // Feature Actions
  minimizeIfHasProducts: (products: any[] | undefined) => void;
  processAudioData: (audioBlob: Blob) => Promise<void>;
}

type VoiceChatStore = VoiceChatState & VoiceChatActions;

// Utility Functions
const isMobileDevice = (): boolean => {
  const userAgent =
    navigator.userAgent || navigator.vendor || (window as any).opera;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
  const isAndroid = /android/i.test(userAgent);
  return isIOS || isAndroid;
};

const audioBlobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const arrayBuffer = reader.result as ArrayBuffer;
      const base64Audio = btoa(
        new Uint8Array(arrayBuffer).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          ""
        )
      );
      resolve(base64Audio);
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(blob);
  });
};

// WAV conversion utility artık use-voice-detection hook'unda mevcut

// WAV utility moved to hook - removing duplicate

export const useVoiceChatStore = create<VoiceChatStore>()(
  persist(
    (set, get) => ({
      // Initial State
      isVoiceChatActive: false,
      isVoiceChatVisible: true,
      isVoiceChatMinimized: false,
      isListening: false,
      isProcessing: false,
      isWaitingForResponse: false,
      transcript: "",
      error: "",
      audioLevel: 0,
      isSpeaking: false,
      isTTSLoading: false,
      hasActiveWidget: false,
      autoMinimizeOnProducts: true,
      isMobile: isMobileDevice(),

      // Voice Chat Controls
      startVoiceChat: async () => {
        // 🚧 Voice chat geçici olarak devre dışı
        set({ error: "Voice chat geçici olarak kullanılamıyor" });
        return;

        // try {
        //   set({
        //     isVoiceChatActive: true,
        //     isVoiceChatVisible: true,
        //     isVoiceChatMinimized: false,
        //     error: "",
        //     transcript: "",
        //   });
        // } catch (error) {
        //   console.error("Error starting voice chat:", error);
        //   set({ error: "Mikrofon başlatılamadı" });
        // }
      },

      stopVoiceChat: () => {
        set({
          isVoiceChatActive: false,
          isListening: false,
          isProcessing: false,
          isWaitingForResponse: false,
          audioLevel: 0,
        });
      },

      closeVoiceChat: () => {
        // ✅ TTS'yi de durdur ve tüm state'i temizle (tek set çağrısı ile)
        set({
          isVoiceChatActive: false,
          isVoiceChatVisible: false,
          isVoiceChatMinimized: false,
          isListening: false,
          isProcessing: false,
          isWaitingForResponse: false,
          isSpeaking: false, // TTS'yi de durdur
          isTTSLoading: false, // TTS loading'i de sıfırla
          transcript: "",
          error: "",
          audioLevel: 0,
        });
      },

      hideVoiceChat: () => set({ isVoiceChatVisible: false }),
      showVoiceChat: () => set({ isVoiceChatVisible: true }),
      minimizeVoiceChat: () => set({ isVoiceChatMinimized: true }),
      maximizeVoiceChat: () => set({ isVoiceChatMinimized: false }),

      // Transcript Actions
      clearTranscript: () => set({ transcript: "" }),
      resetTranscript: () => set({ transcript: "", error: "" }),

      submitTranscript: (text?: string) => {
        const state = get();
        const transcriptToSubmit = text || state.transcript;
        if (transcriptToSubmit.trim()) {
          // Here you would call your submission handler

          set({
            transcript: "",
            isProcessing: false,
            isWaitingForResponse: true,
          });
        }
      },

      // TTS Actions
      speak: async (text: string) => {
        const apiKey = import.meta.env.VITE_GOOGLE_TTS_API_KEY;
        const language = useUIPreferencesStore.getState().language;

        if (!apiKey || !text.trim()) {
          console.error("API anahtarı veya metin eksik");
          return;
        }

        try {
          set({ isTTSLoading: true, error: "" });

          const languageCode =
            language === "Turkish"
              ? "tr-TR"
              : language === "Italian"
                ? "it-IT"
                : "en-US";
          const voiceName =
            language === "Turkish"
              ? "tr-TR-Wavenet-E"
              : language === "Italian"
                ? "it-IT-Wavenet-A"
                : "en-US-Wavenet-F";

          const response = await axios.post(
            `https://texttospeech.googleapis.com/v1/text:synthesize?key=${apiKey}`,
            {
              input: { text },
              voice: {
                languageCode,
                name: voiceName,
                ssmlGender: "FEMALE",
              },
              audioConfig: {
                audioEncoding: "MP3",
                speakingRate: 1.1,
                pitch: 2.0,
              },
            }
          );

          if (response.data.audioContent) {
            const audio = new Audio(
              `data:audio/mp3;base64,${response.data.audioContent}`
            );

            set({ isSpeaking: true, isTTSLoading: false });

            await new Promise<void>((resolve, reject) => {
              audio.onended = () => {
                set({ isSpeaking: false });
                resolve();
              };
              audio.onerror = () => {
                set({ isSpeaking: false, error: "Ses çalınamadı" });
                reject(new Error("Audio playback failed"));
              };
              audio.play().catch(reject);
            });
          }
        } catch (error) {
          console.error("TTS hatası:", error);
          set({
            isSpeaking: false,
            isTTSLoading: false,
            error: "Metin-konuşma dönüştürme hatası",
          });
        }
      },

      stopSpeaking: () => {
        // Note: Audio reference would need to be managed differently in this store approach
        set({ isSpeaking: false, isTTSLoading: false });
      },

      // State Setters
      setHasActiveWidget: (value: boolean) => set({ hasActiveWidget: value }),
      setAutoMinimizeOnProducts: (value: boolean) =>
        set({ autoMinimizeOnProducts: value }),
      setIsListening: (value: boolean) => set({ isListening: value }),
      setIsProcessing: (value: boolean) => set({ isProcessing: value }),
      setIsWaitingForResponse: (value: boolean) =>
        set({ isWaitingForResponse: value }),
      setTranscript: (value: string) => set({ transcript: value }),
      setError: (value: string) => set({ error: value }),
      setAudioLevel: (value: number) => set({ audioLevel: value }),
      setIsSpeaking: (value: boolean) => set({ isSpeaking: value }),
      setIsTTSLoading: (value: boolean) => set({ isTTSLoading: value }),
      setIsMobile: (value: boolean) => set({ isMobile: value }),

      // Feature Actions
      minimizeIfHasProducts: (products: any[] | undefined) => {
        const { autoMinimizeOnProducts } = get();
        if (autoMinimizeOnProducts && products && products.length > 0) {
          set({ isVoiceChatMinimized: true });
        }
      },

      processAudioData: async (audioBlob: Blob) => {
        const apiKey = import.meta.env.VITE_GOOGLE_STT_API_KEY;
        const language = useUIPreferencesStore.getState().language;

        if (!apiKey) {
          set({ error: "STT API anahtarı bulunamadı", isProcessing: false });
          return;
        }

        try {
          set({ isProcessing: true, error: "" });

          const base64Audio = await audioBlobToBase64(audioBlob);
          const languageCode =
            language === "Turkish"
              ? "tr-TR"
              : language === "Italian"
                ? "it-IT"
                : "en-US";

          const response = await axios.post(
            `https://speech.googleapis.com/v1/speech:recognize?key=${apiKey}`,
            {
              config: {
                encoding: "WEBM_OPUS",
                sampleRateHertz: 16000,
                languageCode,
                enableAutomaticPunctuation: true,
                model: "latest_short",
              },
              audio: {
                content: base64Audio,
              },
            }
          );

          if (response.data.results && response.data.results.length > 0) {
            const transcriptText =
              response.data.results[0].alternatives[0].transcript;
            set({ transcript: transcriptText, isProcessing: false });
          } else {
            set({ error: "Ses algılanmadı", isProcessing: false });
          }
        } catch (error) {
          console.error("STT hatası:", error);
          set({
            error: "Konuşma-metin dönüştürme hatası",
            isProcessing: false,
          });
        }
      },
    }),
    {
      name: "voice-chat-storage",
      partialize: (state) => ({
        autoMinimizeOnProducts: state.autoMinimizeOnProducts,
        // Sadece kalıcı olması gereken state'leri persist et
      }),
    }
  )
);
